/* eslint-disable @typescript-eslint/no-explicit-any -- TanStack Form fields and events require explicit any in back-office components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Button } from '../../../../components/ui/Button';
import { Input, FormField } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useToast } from '../../../../components/ui/Toast';
import { Decimal } from 'decimal.js';
import { useCreateGroupDeposit, useGroupDeposits, useAccountsList } from '../../../../features/finance/hooks';
import { useSchemes, type SchemeListItem } from '../../../../features/schemes/network/schemes.api';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';
import React from 'react';

export const Route = createFileRoute('/_auth/finance/group-deposits/new')({
  component: NewGroupDepositPage,
});

const OPTIONS = [
  { value: 'DEPOSIT', label: 'New FD' },
  { value: 'INTEREST', label: 'Post Interest' },
  { value: 'CLOSE', label: 'Close / Mature' },
] as const;

const formSchema = z.object({
  option: z.enum(['DEPOSIT', 'INTEREST', 'CLOSE']),
  schemeId: z.string().optional(),
  parentDepositId: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  amountRupees: z.string().min(1, 'Amount is required'),
  fdrNo: z.string().optional(),
  accountNo: z.string().optional(),
  interestMode: z.string().optional(),
  interestRate: z.string().optional(),
  period: z.string().optional(),
  interestFromDate: z.string().optional(),
  interestUptoDate: z.string().optional(),
  interestDueDate: z.string().optional(),
  transferLedgerId: z.string().optional(),
  tdsRupees: z.string().optional(),
  interestCreditRupees: z.string().optional(),
  preCloserChargesRupees: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const formConfig = defineFormPage<FormValues>({
  resource: 'group-deposits',
  defaultValues: {
    option: 'DEPOSIT',
    schemeId: '',
    parentDepositId: '',
    date: new Date().toISOString().split('T')[0],
    amountRupees: '',
    fdrNo: '',
    accountNo: '',
    interestMode: 'SIMPLE',
    interestRate: '7.0',
    period: '12 Months',
    interestFromDate: '',
    interestUptoDate: '',
    interestDueDate: '',
    transferLedgerId: '',
    tdsRupees: '0',
    interestCreditRupees: '0',
    preCloserChargesRupees: '0',
    notes: '',
  },
  schema: formSchema,
});

interface GroupDepositRow {
  id: string;
  depositNumber: string;
  schemeId: string;
  amountPaise: number;
  option: string;
  status: string;
  depositDate: string;
  fdrNo?: string | null;
  interestRate?: string | null;
  interestDueDate?: string | null;
  notes?: string | null;
}

function NewGroupDepositPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateGroupDeposit();

  const { data: schemesResponse } = useSchemes({ limit: 200 });
  const schemes = schemesResponse?.data;
  const openDepositsQuery = useGroupDeposits({ status: 'OPEN', option: 'DEPOSIT', limit: 200 });
  const openDeposits = (openDepositsQuery.data?.data ?? []) as GroupDepositRow[];
  const { data: accounts } = useAccountsList();
  const cashBankAccounts = (accounts || []).filter(a =>
    a.type === 'asset' && (a.code?.startsWith('cash') || a.code?.startsWith('bank') || a.code?.startsWith('current'))
  );

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(formConfig, createMutation, {
    routeId: 'finance/group-deposits/new',
    onBeforeSubmit: (value) => {
      const option = value.option as 'DEPOSIT' | 'INTEREST' | 'CLOSE';
      const paise = new Decimal(value.amountRupees || '0').mul(100).round().toNumber();

      if (option === 'DEPOSIT') {
        if (!value.schemeId) throw new Error('Scheme is required for FD deposit');
        if (paise <= 0) throw new Error('Amount must be positive');
        return {
          option: 'DEPOSIT',
          date: value.date || new Date().toISOString().split('T')[0],
          schemeId: value.schemeId,
          amountPaise: paise,
          fdrNo: value.fdrNo || undefined,
          accountNo: value.accountNo || undefined,
          interestMode: value.interestMode || undefined,
          interestRate: value.interestRate ? new Decimal(value.interestRate).div(100).toNumber() : undefined,
          period: value.period || undefined,
          interestFromDate: value.interestFromDate || undefined,
          interestUptoDate: value.interestUptoDate || undefined,
          interestDueDate: value.interestDueDate || undefined,
          transferLedgerId: value.transferLedgerId || undefined,
          notes: value.notes || undefined,
        } as never;
      }

      if (option === 'INTEREST') {
        if (!value.parentDepositId) throw new Error('Parent FD is required');
        if (paise <= 0) throw new Error('Interest amount must be positive');
        const tdsPaise = new Decimal(value.tdsRupees || '0').mul(100).round().toNumber();
        return {
          option: 'INTEREST',
          date: value.date || new Date().toISOString().split('T')[0],
          parentDepositId: value.parentDepositId,
          amountPaise: paise,
          tdsAmountPaise: tdsPaise,
          interestFromDate: value.interestFromDate || undefined,
          interestUptoDate: value.interestUptoDate || undefined,
          notes: value.notes || undefined,
        } as never;
      }

      if (option === 'CLOSE') {
        if (!value.parentDepositId) throw new Error('Parent FD is required');
        if (paise <= 0) throw new Error('Maturity proceeds must be positive');
        if (!value.transferLedgerId) throw new Error('Receiving account is required');
        const tdsPaise = new Decimal(value.tdsRupees || '0').mul(100).round().toNumber();
        const interestCreditPaise = new Decimal(value.interestCreditRupees || '0').mul(100).round().toNumber();
        const preCloserPaise = new Decimal(value.preCloserChargesRupees || '0').mul(100).round().toNumber();
        return {
          option: 'CLOSE',
          date: value.date || new Date().toISOString().split('T')[0],
          parentDepositId: value.parentDepositId,
          amountPaise: paise,
          transferLedgerId: value.transferLedgerId,
          tdsAmountPaise: tdsPaise,
          interestCreditAmountPaise: interestCreditPaise,
          preCloserChargesPaise: preCloserPaise,
          notes: value.notes || undefined,
        } as never;
      }

      throw new Error('Invalid option');
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Group Deposit Recorded',
        description: 'The group deposit transaction has been posted with ledger entries.',
      });
      void navigate({ to: '/finance/group-deposits' });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Failed to Record',
        description: err?.message || 'Could not record the group deposit transaction.',
      });
    },
  });

  const option = form.state.values?.option || 'DEPOSIT';

  return (
    <FormPageLayout
      title="Record Group Fixed Deposit"
      subtitle="Log FD placements, post interest accruals, or process maturity closures with automated double-entry ledger postings."
      backTo="/finance/group-deposits"
      cardClassName="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => clearDraft(),
                }}
              />
            )}

            <form.Field
              name="option"
              children={(field: any) => (
                <div className="flex gap-1 p-1 bg-surface-2 rounded-lg w-fit border border-border">
                  {OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => {
                        field.handleChange(opt.value);
                        form.setFieldValue('parentDepositId' as never, '' as never);
                        form.setFieldValue('amountRupees' as never, '' as never);
                      }}
                      className={field.state.value === opt.value ? 'bg-surface-1 shadow-sm border border-border' : 'border-transparent'}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="date"
                validators={{ onChange: ({ value }: { value: any }) => value ? undefined : 'Date is required' }}
                children={(field: any) => (
                  <FormField label="Transaction Date" required>
                    <Input
                      type="date"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            </div>

            {option === 'DEPOSIT' && (
              <>
                <form.Field
                  name="schemeId"
                  children={(field: any) => (
                    <FormSelect
                      label="Chit Scheme"
                      required
                      options={(schemes || []).map((s: SchemeListItem) => ({
                        value: s.id,
                        label: `${s.schemeNumber} - ${s.name}`,
                      }))}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <form.Field
                    name="amountRupees"
                    children={(field: any) => (
                      <FormField label="FD Principal (₹)" required>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="e.g. 500000"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="fdrNo"
                    children={(field: any) => (
                      <FormField label="FDR / FD Number">
                        <Input
                          placeholder="e.g. FD-SBI-88123"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <form.Field
                    name="accountNo"
                    children={(field: any) => (
                      <FormField label="FD Account Number">
                        <Input
                          placeholder="e.g. 50100123456789"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="interestMode"
                    children={(field: any) => (
                      <FormSelect
                        label="Interest Mode"
                        options={[
                          { value: 'SIMPLE', label: 'Simple Interest' },
                          { value: 'CUMULATIVE', label: 'Cumulative / Reinvested' },
                        ]}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e: any) => field.handleChange(e.target.value)}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <form.Field
                    name="interestRate"
                    children={(field: any) => (
                      <FormField label="Interest Rate (% p.a.)">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g. 7.0"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="period"
                    children={(field: any) => (
                      <FormField label="Period">
                        <Input
                          placeholder="e.g. 12 Months"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="interestDueDate"
                    children={(field: any) => (
                      <FormField label="Maturity Date">
                        <Input
                          type="date"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <form.Field
                    name="interestFromDate"
                    children={(field: any) => (
                      <FormField label="Interest From Date">
                        <Input
                          type="date"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="interestUptoDate"
                    children={(field: any) => (
                      <FormField label="Interest Up To Date">
                        <Input
                          type="date"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />
                </div>

                <form.Field
                  name="transferLedgerId"
                  children={(field: any) => (
                    <FormSelect
                      label="Funded From Account (Optional)"
                      options={(cashBankAccounts || []).map((a: any) => ({
                        value: a.id,
                        label: `${a.name} (${a.code})`,
                      }))}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  )}
                />

                <form.Field
                  name="notes"
                  children={(field: any) => (
                    <FormField label="Notes">
                      <textarea
                        className="w-full min-h-[60px] p-2 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 placeholder:text-text-muted focus:ring-1 focus:ring-accent-500"
                        placeholder="Optional notes about this FD placement..."
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e: any) => field.handleChange(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </FormField>
                  )}
                />
              </>
            )}

            {option === 'INTEREST' && (
              <>
                <form.Field
                  name="parentDepositId"
                  children={(field: any) => (
                    <FormSelect
                      label="Parent Fixed Deposit"
                      required
                      options={openDeposits.map((d: GroupDepositRow) => ({
                        value: d.id,
                        label: `${d.depositNumber}${d.fdrNo ? ` (${d.fdrNo})` : ''} - ${new Decimal(d.amountPaise).div(100).toFixed(2)}`,
                      }))}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <form.Field
                    name="amountRupees"
                    children={(field: any) => (
                      <FormField label="Interest Amount (₹)" required>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="e.g. 35000"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="tdsRupees"
                    children={(field: any) => (
                      <FormField label="TDS Deducted (₹)">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g. 3500"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <form.Field
                    name="interestFromDate"
                    children={(field: any) => (
                      <FormField label="Interest Period From">
                        <Input
                          type="date"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="interestUptoDate"
                    children={(field: any) => (
                      <FormField label="Interest Period To">
                        <Input
                          type="date"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />
                </div>

                <form.Field
                  name="notes"
                  children={(field: any) => (
                    <FormField label="Notes">
                      <textarea
                        className="w-full min-h-[60px] p-2 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 placeholder:text-text-muted focus:ring-1 focus:ring-accent-500"
                        placeholder="Optional notes about this interest posting..."
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e: any) => field.handleChange(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </FormField>
                  )}
                />
              </>
            )}

            {option === 'CLOSE' && (
              <>
                <form.Field
                  name="parentDepositId"
                  children={(field: any) => (
                    <FormSelect
                      label="Fixed Deposit to Close"
                      required
                      options={openDeposits.map((d: GroupDepositRow) => ({
                        value: d.id,
                        label: `${d.depositNumber}${d.fdrNo ? ` (${d.fdrNo})` : ''} - ${new Decimal(d.amountPaise).div(100).toFixed(2)}`,
                      }))}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: any) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <form.Field
                    name="amountRupees"
                    children={(field: any) => (
                      <FormField label="Maturity Proceeds Received (₹)" required>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="e.g. 525000"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="transferLedgerId"
                    children={(field: any) => (
                      <FormSelect
                        label="Credit To Account"
                        required
                        options={(cashBankAccounts || []).map((a: any) => ({
                          value: a.id,
                          label: `${a.name} (${a.code})`,
                        }))}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e: any) => field.handleChange(e.target.value)}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <form.Field
                    name="interestCreditRupees"
                    children={(field: any) => (
                      <FormField label="Interest Credit (₹)">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g. 25000"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="tdsRupees"
                    children={(field: any) => (
                      <FormField label="TDS on Interest (₹)">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g. 2500"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />

                  <form.Field
                    name="preCloserChargesRupees"
                    children={(field: any) => (
                      <FormField label="Pre-closure Charges (₹)">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g. 500"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e: any) => field.handleChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </FormField>
                    )}
                  />
                </div>

                <form.Field
                  name="notes"
                  children={(field: any) => (
                    <FormField label="Notes">
                      <textarea
                        className="w-full min-h-[60px] p-2 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 placeholder:text-text-muted focus:ring-1 focus:ring-accent-500"
                        placeholder="Optional notes about this FD closure..."
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e: any) => field.handleChange(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </FormField>
                  )}
                />
              </>
            )}

            <FormActions
              onCancel={() => void navigate({ to: '/finance/group-deposits' })}
              isSubmitting={isSubmitting}
              submitLabel={option === 'DEPOSIT' ? 'Record FD Placement' : option === 'INTEREST' ? 'Post Interest' : 'Close & Mature'}
            />
          </form>
    </FormPageLayout>
  );
}
